import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { cn } from "@wa/ui";
import {
  PORTAL_MODULES,
  grantAllows,
  grantedModules,
  type PortalModule,
  type PortalModuleGrant,
} from "@wa/core/portal-modules";
import { useT } from "../../../i18n/I18nProvider";
import { setContactPortalModules, type Contact } from "../../../lib/clients";
import { GrantPills } from "./GrantPills";

type T = ReturnType<typeof useT>;

/**
 * The BOTTOM level of the portal module grant, for one contact.
 *
 * Three levels decide this now: a CRM-wide default, this company's override, and
 * this person's row. The first two arrive here already resolved as
 * `companyGrant` (`resolveCompanyGrant`), and this control edits the third. The
 * contact level INTERSECTS the company, so everything here can only narrow -
 * which is precisely what makes a company-level empty list a real kill switch
 * instead of a setting that loses to every contact holding an explicit array.
 *
 * ── INHERITED IS NOT THE SAME CLAIM AS UNRESTRICTED ────────────────────────
 *
 * `null` on this row used to be printed as "All modules", and it was true while
 * this was the only level there was. It is a lie the moment a default or a
 * client override exists: the row would promise five modules to a person at a
 * company that was sold two. `null` says nothing about how much access there is;
 * it says only that THIS row is not the place the answer was decided. So the
 * collapsed line now reads as inherited and carries the count the server
 * resolved, and the pills below draw the company's closed modules struck through
 * rather than ticked. The difference matters in the direction that hurts: a
 * screen that overstates access is one an operator stops double-checking.
 *
 * ── WHY THIS IS NOT A ROW OF SWITCHES ON THE CONTACT ───────────────────────
 *
 * The contact row is already carrying a name, a role, a mail address, a phone
 * number, an access badge and a last-seen date. Five more controls there would
 * push the row past what fits and, worse, would make an unrestricted contact look
 * configured - five ticked boxes and a stripped-to-nothing contact would be the
 * same visual weight. So the collapsed state is ONE line that answers the only
 * question the operator usually has ("all of it, or some of it"), and the boxes
 * appear when they are being changed.
 *
 * ── THE THREE STATES ARE DRAWN DIFFERENTLY ON PURPOSE ──────────────────────
 *
 * `null` is "this row restricts nothing" and reads as plain text, because it is
 * the default and nobody decided it here. A partial grant is the one worth
 * noticing, so it is the one that gets a colour. An empty grant is a person who
 * can sign in and see nothing, which is legal, rare, and much more often a
 * mistake than an intention - it says so in as many words rather than showing
 * "0 of 5" and leaving the operator to work out whether that is what they meant.
 *
 * Saving is immediate, one PUT per tick. A Save button would add a fourth state
 * (unsaved) to a control whose whole job is to make the current one obvious. The
 * client and global panels choose the opposite policy for their own reasons; the
 * shared `GrantPills` deliberately leaves that choice to each caller.
 */
export function ContactPortalModules({ clientId, contact, companyGrant, canManage, onChange }: {
  clientId: string;
  contact: Contact;
  /**
   * What this company may open, default already folded in. Not the raw client
   * row: a client that decided nothing inherits the CRM-wide default, and the
   * parent is the level that knows both.
   */
  companyGrant: PortalModuleGrant;
  canManage: boolean;
  onChange: () => void;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  // The grant as the server last confirmed it. Held locally so a tick lands at
  // once instead of waiting for the parent's reload, which refetches the whole
  // client.
  const [grant, setGrant] = useState<PortalModuleGrant>(contact.portalModules ?? null);

  async function save(next: PortalModuleGrant) {
    const previous = grant;
    setGrant(next);
    setBusy(true);
    setFailed(false);
    try {
      await setContactPortalModules(clientId, contact.id, next);
      onChange();
    } catch {
      // Put the old value back. A control that keeps showing the tick the
      // server refused is a screen telling the operator access was granted
      // when it was not.
      setGrant(previous);
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }

  const label = summaryLabel(t, grant, contact, companyGrant);

  return (
    <div className="mt-1.5">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={!canManage}
        className={cn(
          "inline-flex items-center gap-1 text-xs transition-colors",
          canManage ? "hover:text-foreground" : "cursor-default",
          grant === null
            ? "text-muted-foreground"
            : grant.length === 0
              ? "text-red-600 dark:text-red-400"
              : "text-amber-700 dark:text-amber-300",
        )}
        title={t("clients.portalModules.hint")}
      >
        <ShieldCheck className="size-3" />
        {label}
        {busy && <Loader2 className="size-3 animate-spin" />}
      </button>

      {open && canManage && (
        <div className="mt-1.5">
          <GrantPills
            items={PORTAL_MODULES}
            grant={grant}
            onChange={(next) => void save(next)}
            labelFor={(m) => t(`clients.portalModules.m.${m}`)}
            // The company's set, so the first untick out of `null` subtracts one
            // module instead of writing the company's closed ones back into this
            // person's row.
            baseWhenNull={grantedModules(companyGrant)}
            unavailable={PORTAL_MODULES.filter((m) => !grantAllows(companyGrant, m))}
            clearLabel={t("clients.portalModules.clear")}
            disabled={busy}
          />
        </div>
      )}

      {failed && <p className="mt-1 text-[11px] text-red-600 dark:text-red-400">{t("clients.portalModules.saveFailed")}</p>}
    </div>
  );
}

/**
 * How many modules this contact actually gets while their own row decides
 * nothing. Only meaningful in the `grant === null` branch below.
 *
 * The server's number first. `portalModulesEffective` comes out of
 * `resolveModuleGrant`, the single place the precedence rule lives; working it
 * out again in the browser would be a second copy of that rule that nothing can
 * check against the first, and two copies that agree today are a hazard rather
 * than a reassurance.
 *
 * Read through a widening cast because `Contact` in `lib/clients.ts` does not
 * declare the field yet - it is being added in a parallel change, and the cast
 * keeps compiling once it lands.
 *
 * The fallback covers two cases and is exactly right in both: a response that
 * carries no such field (an older build, or a route that does not send it), and
 * the moment straight after the clear pill is clicked, when the local grant is
 * already null but the parent's snapshot still holds the count from the row that
 * was just cleared. For a null contact grant the effective set IS the company
 * grant, so the fallback is the same arithmetic done locally.
 */
function inheritedCount(contact: Contact, companyGrant: PortalModuleGrant): number {
  const carried = (contact as { portalModulesEffective?: PortalModule[] }).portalModulesEffective;
  const stored = contact.portalModules ?? null;
  if (Array.isArray(carried) && stored === null) return carried.length;
  return grantedModules(companyGrant).length;
}

function summaryLabel(t: T, grant: PortalModuleGrant, contact: Contact, companyGrant: PortalModuleGrant): string {
  const total = String(PORTAL_MODULES.length);
  if (grant === null) {
    return t("clients.portalModules.inherited", { n: String(inheritedCount(contact, companyGrant)), total });
  }
  if (grant.length === 0) return t("clients.portalModules.none");
  return t("clients.portalModules.some", { n: String(grant.length), total });
}
